import {
  BD_UI_FILE_INPUT_CONFIG_DEFAULT,
  BdUiFileInputConfig,
} from '@bindoc-projects/ui-lib/file/ui';
import {
  Component,
  Input,
  output as output_1,
  ChangeDetectionStrategy,
} from '@angular/core';
import { KostDataImportProcessDataDe } from '../../import-process';
import {
  KOST_DATA_IMPORT_DE_INPUT_STEPS,
  KostDataImportDatasetInputProcessorDe,
} from './de-input-processor';
import { KostDataImportDeNewDatasetInputFabCreationStrategySelectComponent } from './fab-creation-strategy-select.component';
import { AysFabCreationStrategy } from '@bindoc-projects/virtual-clinic-domain-lib';
import { KostDataImportNewDatasetInputComponent } from '@bindoc-projects/kost-data-import-lib/ui';

@Component({
  selector: 'kost-data-import-de-new-dataset-input',
  template: `
    <kost-data-import-new-dataset-input
      [config]="config"
      [processData]="processData"
      [processor]="processor"
      (update)="onProcessorResult($event)"
    >
    </kost-data-import-new-dataset-input>

    @if (
      processData.data.input &&
      (!processData.data.output.errors ||
        processData.data.output.errors.length === 0) &&
      processData.data.output.additionalInformation.fabPlusDetected
    ) {
      <kost-data-import-de-new-dataset-input-fab-creation-strategy-select
        class="mt-6"
        [fabCreationStrategy]="processData.parameters.data.fabCreationStrategy"
        (fabCreationStrategyChange)="onFabCreationStrategyChange($event)"
      >
      </kost-data-import-de-new-dataset-input-fab-creation-strategy-select>
    }
  `,

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    KostDataImportDeNewDatasetInputFabCreationStrategySelectComponent,
    KostDataImportNewDatasetInputComponent,
  ],
})
export class KostDataImportDeNewDatasetInputComponent {
  @Input()
  public processData: KostDataImportProcessDataDe;

  public readonly update = output_1<{
    data: KostDataImportProcessDataDe;
    complete: boolean;
  }>();

  public readonly config: {
    fileInput: BdUiFileInputConfig;
    useWorker: boolean;
    steps: Readonly<Record<string, string>>;
  } = {
    fileInput: {
      ...BD_UI_FILE_INPUT_CONFIG_DEFAULT,
      accept: '.zip',
    },
    useWorker: true,
    steps: KOST_DATA_IMPORT_DE_INPUT_STEPS,
  };

  public processor: (() => Worker) | KostDataImportDatasetInputProcessorDe;

  constructor() {
    this._initProcessor();
  }

  public onProcessorResult(data: {
    input: File;
    output: {
      transformed: Blob;
      additionalInformation: unknown;
      additionalArtifacts: { content: File | Blob; displayName: string }[];
    };
  }) {
    this.processData.data = data as {
      input: File;
      output: {
        errors?: string[];
        transformed: Blob;
        additionalInformation: {
          fabPlusDetected: boolean;
        };
        additionalArtifacts: { content: File | Blob; displayName: string }[];
      };
    };

    this.update.emit({
      data: this.processData,
      complete: this._isCompleted(),
    });
  }

  public onFabCreationStrategyChange(strategy: AysFabCreationStrategy): void {
    this.processData.parameters.data.fabCreationStrategy = strategy;

    this.update.emit({
      data: this.processData,
      complete: this._isCompleted(),
    });
  }

  private _initProcessor(): void {
    let processor: KostDataImportDatasetInputProcessorDe | (() => Worker);

    if (typeof Worker !== 'undefined') {
      processor = () => {
        return new Worker(
          new URL('./de-input-processor.worker', import.meta.url),
          {
            type: 'module',
          },
        );
      };
    } else {
      processor = new KostDataImportDatasetInputProcessorDe();
    }

    this.processor = processor;
  }

  private _isCompleted(): boolean {
    return (
      Boolean(this.processData.data.input) &&
      Boolean(this.processData.data.output.transformed)
    );
  }
}
